import type { EntityAdapter, EntityState } from '@ngrx/entity';
import { createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import type { SchoolActivityInstanceOverview } from '../../../services/flex-daily-overview.service';
import { UpdateSchoolActivityInstanceSuccessAction } from '../../school-activities-instances/actions/school-activities-instances.actions';
import {
	ClearSchoolActivityInstancesOverviewAction,
	GetSchoolActivityInstancesOverviewSuccessAction,
} from '../actions/school-activities-instances-overview.actions';

export const schoolActivityInstancesOverviewInitialState: EntityState<SchoolActivityInstanceOverview> = {
	ids: [],
	entities: {},
};
export const schoolActivityInstanceOverviewAdapter: EntityAdapter<SchoolActivityInstanceOverview> =
	createEntityAdapter<SchoolActivityInstanceOverview>();

export const schoolActivitiesInstanceOverviewReducer = createReducer(
	schoolActivityInstancesOverviewInitialState,
	on(UpdateSchoolActivityInstanceSuccessAction, (state, { instance }) => {
		let existing = Object.values(state.entities).find((e) => e && e.id === instance.id);
		if (existing) {
			existing = { ...existing, ...instance };
		}
		return existing ? schoolActivityInstanceOverviewAdapter.upsertOne(existing, state) : state;
	}),
	on(GetSchoolActivityInstancesOverviewSuccessAction, (state, { instances, overviewDate }) => {
		let idsToRemove: string[] = [];
		if (state.ids.length > 0) {
			idsToRemove = (state.ids as string[]).filter((id: string) => {
				const instance = state.entities[id];
				return instance && !instance.overviewDate.isSame(overviewDate, 'day');
			});
		}
		const newState = schoolActivityInstanceOverviewAdapter.removeMany(idsToRemove, state);
		return schoolActivityInstanceOverviewAdapter.upsertMany(instances, {
			...newState,
		});
	}),
	on(ClearSchoolActivityInstancesOverviewAction, (state, { scheduleType, flexPeriodId, scheduleId }) => {
		const idsToRemove = Object.values(state.entities)
			.filter((instance): instance is SchoolActivityInstanceOverview => {
				if (!instance) return false;

				if (scheduleType === 'custom') {
					return instance.activity_schedule_type === 'custom';
				} else if (flexPeriodId) {
					const matchesFlexPeriod = instance.flex_period_id === flexPeriodId;

					// If scheduleId is provided, filter by it
					if (scheduleId !== undefined) {
						return matchesFlexPeriod && instance.schedule_id === scheduleId;
					}

					// Defensive: if no scheduleId provided, only clear instances without schedule_id
					// This prevents accidentally clearing other schedules' data when multiple
					// schedule-specific components are active for the same flex period
					return matchesFlexPeriod && instance.schedule_id === undefined;
				}
				return false;
			})
			.map((instance) => instance.id);

		return schoolActivityInstanceOverviewAdapter.removeMany(idsToRemove, state);
	})
);
